import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { generateUrl } from 'utils/helpers';
import { apiLoaderQuery } from 'store/query';

import {
  RegistrationForm,
  CaseDataResponse,
  GetRegistrationFormResponse,
  SendRegistrationLinkRequest,
  SendRegistrationLinkResponse,
  CustomerFormSubmissionRequest,
  PublishRegistrationFormRequest,
  CustomerFormSubmissionResponse,
  PublishRegistrationFormResponse,
} from './types';

const tagType = 'RegistrationForms';

export const registrationFormsApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiLoaderQuery,
  reducerPath: 'registrationFormsApi',
  endpoints: (build) => ({
    // Get case data for prefilling form
    getCaseData: build.query<CaseDataResponse, { token: string }>({
      transformResponse: (response: CaseDataResponse) => response,
      query: ({ token }) => ({
        method: 'GET',
        url: `${URL.GET_CASE_DATA}/${token}`,
      }),
    }),

    // Send registration form link
    sendLink: build.mutation<
      SendRegistrationLinkResponse,
      SendRegistrationLinkRequest
    >({
      transformResponse: (response: SendRegistrationLinkResponse) => response,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.SEND_REGISTRATION_LINK,
      }),
    }),

    // Submit customer form response
    submitCustomerResponse: build.mutation<
      CustomerFormSubmissionResponse,
      CustomerFormSubmissionRequest
    >({
      transformResponse: (response: CustomerFormSubmissionResponse) => response,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.SUBMIT_CUSTOMER_FORM_RESPONSE,
      }),
    }),

    // Get active registration form for hospital
    getActiveForm: build.query<RegistrationForm, { hospitalId: number }>({
      transformResponse: (response: GetRegistrationFormResponse) =>
        response.data.form,
      providesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ hospitalId }) => ({
        method: 'GET',
        url: generateUrl(URL.GET_REGISTRATION_FORM, {
          hospitalId,
        }),
      }),
    }),

    // Publish a registration form (save and mark as published)
    publish: build.mutation<RegistrationForm, PublishRegistrationFormRequest>({
      transformResponse: (response: PublishRegistrationFormResponse) =>
        response.data,
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ title, formData, hospitalId }) => ({
        method: 'post',
        url: URL.PUBLISH_REGISTRATION_FORM,
        data: {
          form_data: formData,
          hospital_id: hospitalId,
          title: title || 'Registration Form',
        },
      }),
    }),
  }),
});

export const {
  useGetCaseDataQuery,
  useGetActiveFormQuery,
  useSubmitCustomerResponseMutation,
  useSendLinkMutation: useSendRegistrationLinkMutation,
  usePublishMutation: usePublishRegistrationFormMutation,
} = registrationFormsApi;

// Export types
export type {
  CustomerFormAnswer,
  CustomerFormSection,
  CustomerFormResponse,
  CustomerFormSubmissionRequest,
  CustomerFormSubmissionResponse,
} from './types';
